/**
 * RoleWizardStepResponsibilities.jsx — Phase 0.6 Slice B Step 2.
 *
 * Writes to `structured_definition_json` via PATCH /employer/roles/{id}:
 *   - responsibilities (list of strings)
 *   - must_have_skills (chips)
 *   - nice_to_have_skills (chips)
 *
 * Reuses `CriteriaChipInput` for the two skill lists (same cap +
 * normalisation semantics as the criteria mirror).
 */
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import CriteriaChipInput, { MAX_CRITERION_LEN as MAX_LEN } from './CriteriaChipInput';

const MAX_RESPONSIBILITIES   = 30;
const MAX_RESPONSIBILITY_LEN = 400;

function ResponsibilityList({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const update = (i, next) => {
    const out = [...items];
    out[i] = next;
    onChange(out);
  };
  const remove = (i) => {
    const out = items.filter((_, idx) => idx !== i);
    onChange(out);
  };
  const add = () => {
    if (items.length >= MAX_RESPONSIBILITIES) return;
    onChange([...items, '']);
  };
  return (
    <div className="space-y-2" data-testid="responsibilities-editor">
      {items.length === 0 ? (
        <p className="text-xs text-slate-300">
          Add at least one responsibility before publishing.
        </p>
      ) : null}
      {items.map((line, i) => (
        <div
          key={`r-${i}`}
          className="flex items-start gap-2"
          data-testid={`responsibility-row-${i}`}
        >
          <Textarea
            value={line}
            onChange={(e) => update(i, e.target.value.slice(0, MAX_RESPONSIBILITY_LEN))}
            rows={2}
            maxLength={MAX_RESPONSIBILITY_LEN + 1}
            placeholder="e.g. Lead reservoir simulation studies for ..."
            data-testid={`responsibility-input-${i}`}
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => remove(i)}
            data-testid={`responsibility-remove-${i}`}
            aria-label="Remove responsibility"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={add}
        disabled={items.length >= MAX_RESPONSIBILITIES}
        data-testid="responsibility-add"
      >
        <Plus className="mr-1 h-4 w-4" />
        Add responsibility
      </Button>
      <p className="text-xs text-slate-300">
        {items.length} / {MAX_RESPONSIBILITIES} entries · {MAX_RESPONSIBILITY_LEN} char max each
      </p>
    </div>
  );
}

export default function RoleWizardStepResponsibilities({
  definition,
  onDefinitionChange,
}) {
  const defn = definition || {};
  const patchDefn = (patch) => onDefinitionChange({ ...defn, ...patch });

  return (
    <div className="space-y-6 p-6" data-testid="wizard-step-responsibilities">
      <header>
        <h2 className="text-base font-semibold text-ndor-ink">
          Responsibilities &amp; skills
        </h2>
        <p className="text-sm text-slate-300">
          What the person will do and what they MUST bring. Nice-to-haves are
          tie-breakers — not gates.
        </p>
      </header>

      <section className="space-y-2">
        <Label className="text-sm">Responsibilities</Label>
        <ResponsibilityList
          value={defn.responsibilities}
          onChange={(v) => patchDefn({ responsibilities: v })}
        />
      </section>

      <section className="space-y-2">
        <Label className="text-sm">Must-have skills</Label>
        <CriteriaChipInput
          value={defn.must_have_skills || []}
          onChange={(v) => patchDefn({ must_have_skills: v })}
          placeholder="Add a must-have skill and press Enter…"
          tone="emerald"
          dataTestid="must-have-skills"
        />
        <p className="text-xs text-slate-300">
          Each up to {MAX_LEN} chars. Required for publish.
        </p>
      </section>

      <section className="space-y-2">
        <Label className="text-sm">Nice-to-have skills</Label>
        <CriteriaChipInput
          value={defn.nice_to_have_skills || []}
          onChange={(v) => patchDefn({ nice_to_have_skills: v })}
          placeholder="Add a nice-to-have and press Enter…"
          tone="sky"
          dataTestid="nice-to-have-skills"
        />
        <p className="text-xs text-slate-300">
          Already-listed must-haves are removed automatically by the backend on save.
        </p>
      </section>
    </div>
  );
}
